/** The texts of the label catalog's editor and its proposals (LBL-01 to LBL-03, LBL-10, LBL-12), spread into the app's dictionaries (`lib/i18n.tsx`). */
export const catalogEn = {
  newLabel: 'New label',
  proposeLabel: 'Propose a label',
  newLabelHint: 'A label keeps its name for good, and its shape type once annotations use it. It is saved with the catalog.',
  labelNameRule: 'Lowercase letters, digits and _',
  labelTaken: 'The catalog has a label of this name.',
  unsavedLabel: 'Not saved',
  retired: 'retired',
  systemTag: 'system tag',
  systemTagHint: 'The platform sets it: it counts how often a user checked the image. Nobody picks it as a label, and it is no finding.',
  labelUsesCount: '{count} uses',
  labelUsesCount_one: '1 use',
  labelAttributesCount: '{count} attributes',
  labelAttributesCount_one: '1 attribute',
  labelInUseHint: 'In use: it keeps its type and every attribute and value it has. Choices can be added and ranges widened; saving asks for confirmation.',
  keyAlsoOf: 'Also the key of {name}',
  labelKeyHint: 'Press a key; Backspace clears it',
  retireLabel: 'Retired',
  retireLabelHint: 'Offered for no new annotations; existing ones keep it. Once nothing uses or names it, it can be deleted.',
  inBoxHeatmap: 'Shown in the box heatmap',
  inBoxHeatmapHint: 'Its boxes show over the thumbnails of the images. Leave out boxes that mark no finding, such as crop boxes.',
  rotatableLabel: 'Boxes can be rotated',
  rotatableLabelHint: 'A selected box gets a handle and an angle field, for findings that lie at a slant. Crop boxes stay upright.',
  studyTag: 'Study tag',
  studyTagHint: 'One answer for the whole study: setting, changing or removing it on one view does so on all its views, and agreement counts it once per study.',
  labelCodes: 'Codes',
  labelCodesHint: 'SNOMED CT or RadLex codes that name the same finding, listed with the label in exports. They change nothing users see.',
  noLabelCodes: 'No codes',
  codeSystem: 'Terminology',
  codeValue: 'Code',
  codeDisplay: 'Name',
  codeDisplayHint: 'As the terminology names it',
  removeCode: 'Remove {code}',
  addCode: 'Add a code',
  codeFormat: 'Not written as a {system} code',
  codeDisplayNeeded: 'Give the name the terminology uses.',
  attributes: 'Attributes',
  noAttributes: 'No attributes',
  removeNewLabel: 'Remove this new label',
  changedMark: 'changed',
  labelsChanged: '{count} labels changed, not saved yet',
  labelsChanged_one: '1 label changed, not saved yet',
  discardCatalogChanges: 'Discard changes',
  fixLabelFirst: 'Complete the attributes and codes of {name} to save.',
  attributeName: 'Attribute name',
  attributeType: 'Answer type',
  input_radio: 'Choice (buttons)',
  input_select: 'Choice (list)',
  input_number: 'Number',
  input_checkbox: 'Checkbox',
  input_text: 'Text',
  removeAttribute: 'Remove {name}',
  attributeTaken: 'Another attribute of this label has this name.',
  choicesNeeded: 'Give at least one choice, each once.',
  numberRange: 'The minimum must lie below the maximum, and the step above 0.',
  numberMinimum: 'Minimum',
  numberMaximum: 'Maximum',
  numberStep: 'Step',
  attributeChoices: 'Choices',
  removeChoice: 'Remove {name}',
  newChoice: 'New choice',
  arrangeHint: 'Drag a label, or press Alt+↑/↓ on it, to change its place.',
  proposeLabelHint: 'The catalog editors decide on it. Until the label exists, name a free-text class for the finding in the tool.',
  proposedAs: 'Proposed as {name}',
  proposalExplanation: 'What it marks and why the catalog needs it',
  sendProposal: 'Send to the catalog editors',
  proposalSent: 'Your proposal is with the catalog editors. You will be notified of their decision.',
  labelProposals: 'Proposals',
  yourProposals: 'Your proposals',
  proposalsWaiting: '{count} proposals await review',
  proposalsWaiting_one: '1 proposal awaits review',
  proposal_pending: 'pending',
  proposal_approved: 'approved',
  proposal_declined: 'declined',
  proposedBy: 'Proposed by {name} on {when}',
  approvedAs: 'Added as {name}',
  declinedBecause: 'Reason: {reason}',
  reviewProposal: 'Review',
  proposalOf: 'Proposal {name}',
  approveProposal: 'Approve and add to the catalog',
  declineReason: 'Reason for declining',
  declineProposal: 'Decline',
  saveCatalogFirst: 'Save or discard the catalog’s changes first.',
  notice_label_proposed: 'A label was proposed: {label}',
  notice_label_proposal_decided: 'Your label proposal {label} was {decision}',
  kind_label_proposed: 'A label is proposed (catalog editors)',
  deleteLabel: 'Delete label',
  deleteLabelHint: 'A retired label that nothing uses or names can be deleted, for example one created by mistake. Its name is free again afterwards.',
  deleteLabelWarning: 'Delete {name} for good?',
  checkingDeletion: 'Checking what uses or names it…',
  deletable: 'Nothing uses or names it: it can be deleted.',
  deleteBlocked: 'It stays until these are resolved:',
  deleteBlocker_active: 'It is not retired: retire it and save first.',
  deleteBlocker_system: 'It is a system tag, which the platform defines.',
  deleteBlocker_used: 'Annotations, first readings, resolutions or learners’ answers use it {count} times.',
  deleteBlocker_used_one: 'Annotations, first readings, resolutions or learners’ answers use it once.',
  deleteBlocker_models: 'Model versions map a class to it: {names}',
  deleteBlocker_profiles: 'Labeling profiles set it up: {names}',
  deleteBlocker_jobs: 'Jobs offer it or ask about it: {names}',
  deleteBlocker_case_sets: 'Case sets offer it: {names}',
  deleteBlocker_renumbers: 'Exports number their classes in the catalog’s order, so deleting it would renumber {names}.',
  namesAndMore: '{names} and {count} more',
  labelDeleted: '{name} was deleted.',
  reloadCatalog: 'Reload the catalog',
}

export const catalogDe: Record<keyof typeof catalogEn, string> = {
  newLabel: 'Neues Label',
  proposeLabel: 'Label vorschlagen',
  newLabelHint: 'Ein Label behält seinen Namen für immer, und seinen Formtyp, sobald Annotationen es verwenden. Es wird mit dem Katalog gespeichert.',
  labelNameRule: 'Kleinbuchstaben, Ziffern und _',
  labelTaken: 'Der Katalog hat ein Label dieses Namens.',
  unsavedLabel: 'Nicht gespeichert',
  retired: 'stillgelegt',
  systemTag: 'Systemtag',
  systemTagHint: 'Die Plattform setzt es: Es zählt, wie oft jemand das Bild geprüft hat. Niemand wählt es als Label, und es ist kein Befund.',
  labelUsesCount: '{count} Verwendungen',
  labelUsesCount_one: '1 Verwendung',
  labelAttributesCount: '{count} Attribute',
  labelAttributesCount_one: '1 Attribut',
  labelInUseHint: 'In Verwendung: Es behält seinen Typ und jedes Attribut und jeden Wert. Auswahlwerte können hinzukommen und Bereiche wachsen; das Speichern verlangt eine Bestätigung.',
  keyAlsoOf: 'Auch die Taste von {name}',
  labelKeyHint: 'Taste drücken; Rücktaste löscht sie',
  retireLabel: 'Stillgelegt',
  retireLabelHint: 'Für keine neuen Annotationen angeboten; bestehende behalten es. Sobald nichts es verwendet oder nennt, lässt es sich löschen.',
  inBoxHeatmap: 'In der Box-Heatmap gezeigt',
  inBoxHeatmapHint: 'Seine Rechtecke erscheinen über den Vorschaubildern der Bilder. Lassen Sie Rechtecke weg, die keinen Befund markieren, etwa Zuschnitt-Rechtecke.',
  rotatableLabel: 'Rechtecke drehbar',
  rotatableLabelHint: 'Ein gewähltes Rechteck erhält einen Griff und ein Winkelfeld, für schräg liegende Befunde. Zuschnitt-Rechtecke bleiben gerade.',
  studyTag: 'Studien-Tag',
  studyTagHint: 'Eine Antwort für die ganze Studie: Auf einer Ansicht gesetzt, geändert oder entfernt, gilt das für alle ihre Ansichten, und die Übereinstimmung zählt ihn einmal pro Studie.',
  labelCodes: 'Codes',
  labelCodesHint: 'SNOMED-CT- oder RadLex-Codes, die denselben Befund benennen, in Exporten beim Label aufgeführt. Sie ändern nichts an dem, was Benutzer sehen.',
  noLabelCodes: 'Keine Codes',
  codeSystem: 'Terminologie',
  codeValue: 'Code',
  codeDisplay: 'Bezeichnung',
  codeDisplayHint: 'Wie die Terminologie ihn bezeichnet',
  removeCode: '{code} entfernen',
  addCode: 'Code hinzufügen',
  codeFormat: 'Nicht wie ein {system}-Code geschrieben',
  codeDisplayNeeded: 'Geben Sie die Bezeichnung der Terminologie an.',
  attributes: 'Attribute',
  noAttributes: 'Keine Attribute',
  removeNewLabel: 'Dieses neue Label entfernen',
  changedMark: 'geändert',
  labelsChanged: '{count} Labels geändert, noch nicht gespeichert',
  labelsChanged_one: '1 Label geändert, noch nicht gespeichert',
  discardCatalogChanges: 'Änderungen verwerfen',
  fixLabelFirst: 'Vervollständigen Sie die Attribute und Codes von {name}, um zu speichern.',
  attributeName: 'Attributname',
  attributeType: 'Antworttyp',
  input_radio: 'Auswahl (Schaltflächen)',
  input_select: 'Auswahl (Liste)',
  input_number: 'Zahl',
  input_checkbox: 'Kontrollkästchen',
  input_text: 'Text',
  removeAttribute: '{name} entfernen',
  attributeTaken: 'Ein anderes Attribut dieses Labels hat diesen Namen.',
  choicesNeeded: 'Mindestens einen Auswahlwert angeben, jeden einmal.',
  numberRange: 'Das Minimum muss unter dem Maximum liegen und der Schritt über 0.',
  numberMinimum: 'Minimum',
  numberMaximum: 'Maximum',
  numberStep: 'Schritt',
  attributeChoices: 'Auswahlwerte',
  removeChoice: '{name} entfernen',
  newChoice: 'Neuer Auswahlwert',
  arrangeHint: 'Ein Label ziehen oder darauf Alt+↑/↓ drücken, um seinen Platz zu ändern.',
  proposeLabelHint: 'Die Katalog-Redaktion entscheidet darüber. Bis es das Label gibt, benennen Sie für den Befund im Werkzeug eine Freitext-Klasse.',
  proposedAs: 'Vorgeschlagen als {name}',
  proposalExplanation: 'Was es markiert und warum der Katalog es braucht',
  sendProposal: 'An die Katalog-Redaktion senden',
  proposalSent: 'Ihr Vorschlag liegt bei der Katalog-Redaktion. Sie werden über die Entscheidung benachrichtigt.',
  labelProposals: 'Vorschläge',
  yourProposals: 'Ihre Vorschläge',
  proposalsWaiting: '{count} Vorschläge warten auf Prüfung',
  proposalsWaiting_one: '1 Vorschlag wartet auf Prüfung',
  proposal_pending: 'offen',
  proposal_approved: 'angenommen',
  proposal_declined: 'abgelehnt',
  proposedBy: 'Vorgeschlagen von {name} am {when}',
  approvedAs: 'Aufgenommen als {name}',
  declinedBecause: 'Begründung: {reason}',
  reviewProposal: 'Prüfen',
  proposalOf: 'Vorschlag {name}',
  approveProposal: 'Annehmen und in den Katalog aufnehmen',
  declineReason: 'Begründung der Ablehnung',
  declineProposal: 'Ablehnen',
  saveCatalogFirst: 'Speichern oder verwerfen Sie zuerst die Änderungen am Katalog.',
  notice_label_proposed: 'Ein Label wurde vorgeschlagen: {label}',
  notice_label_proposal_decided: 'Ihr Label-Vorschlag {label} wurde {decision}',
  kind_label_proposed: 'Ein Label wird vorgeschlagen (Katalog-Redaktion)',
  deleteLabel: 'Label löschen',
  deleteLabelHint: 'Ein stillgelegtes Label, das nichts verwendet oder nennt, lässt sich löschen, etwa eines, das versehentlich angelegt wurde. Sein Name ist danach wieder frei.',
  deleteLabelWarning: '{name} endgültig löschen?',
  checkingDeletion: 'Prüfe, was es verwendet oder nennt …',
  deletable: 'Nichts verwendet oder nennt es: Es lässt sich löschen.',
  deleteBlocked: 'Es bleibt, bis Folgendes geklärt ist:',
  deleteBlocker_active: 'Es ist nicht stillgelegt: Legen Sie es zuerst still und speichern Sie.',
  deleteBlocker_system: 'Es ist ein Systemtag, den die Plattform festlegt.',
  deleteBlocker_used: 'Annotationen, Erstbefundungen, Auflösungen oder Antworten Lernender verwenden es {count}-mal.',
  deleteBlocker_used_one: 'Annotationen, Erstbefundungen, Auflösungen oder Antworten Lernender verwenden es einmal.',
  deleteBlocker_models: 'Modellversionen ordnen ihm eine Klasse zu: {names}',
  deleteBlocker_profiles: 'Labeling-Profile richten es ein: {names}',
  deleteBlocker_jobs: 'Aufträge bieten es an oder fragen danach: {names}',
  deleteBlocker_case_sets: 'Fallsammlungen bieten es an: {names}',
  deleteBlocker_renumbers: 'Exporte nummerieren ihre Klassen in der Reihenfolge des Katalogs; Löschen würde {names} umnummerieren.',
  namesAndMore: '{names} und {count} weitere',
  labelDeleted: '{name} wurde gelöscht.',
  reloadCatalog: 'Katalog neu laden',
}
